/**
 * Client-side flow validator (P-1.14).
 *
 * Implements FLOWS.md § 10 rules 1–23 end-to-end. Runs on every Save /
 * Publish and on every edit (cheap pass) so the designer can highlight
 * errors inline. Backend re-runs its own validator at publish time as the
 * authoritative check.
 */

import type {
  ABTestNode,
  CheckoutPageNode,
  ConditionNode,
  DelayNode,
  FlowDefinition,
  FlowNode,
  FraudCheckNode,
  HttpRequestNode,
  LayoutBlocks,
  NodeType,
  ScheduleNode,
  SwitchNode,
  TriggerNode,
} from "../../types/flow";

export interface ValidationError {
  rule: number;
  node_id: string | null;
  code: string;
  message: string;
}

const MAX_DEPTH = 20;
const MAX_DEFINITION_BYTES = 64 * 1024;

// Canonical block-to-section map from FLOWS.md § 11.9 § C2
const REQUIRED_BLOCKS_BY_SECTION: Record<string, readonly string[]> = {
  contact: ["email"],
  shipping: ["address", "shipping_method"],
  payment: ["payment_method_selector", "pay_button"],
  summary: ["line_items", "totals"],
};

// Generic blocks available in every section (trust badges + utility blocks).
const SHARED_SECTION_BLOCKS = [
  "trust_badges",
  "text_banner",
  "image_banner",
  "divider",
];

// Source of truth: must stay in sync with PageBuilder.tsx's
// AVAILABLE_BLOCKS. Whenever the palette gains a block type for a
// section, add it here too — otherwise publish fails with rule 18
// ("block not valid in section") even though the UI let the merchant
// drop it there. Mirrored by `allowedBlocksBySection` in
// checkout-orchestrator/internal/checkout/flow/validator/validator.go.
export const VALID_BLOCKS_BY_SECTION: Record<string, ReadonlySet<string>> = {
  contact: new Set([
    "email",
    "marketing_consent",
    "newsletter_opt_in",
    "sms_opt_in",
    "phone",
    "company_field",
    "login_link",
    "loyalty_login_card",
    "save_my_info",
    ...SHARED_SECTION_BLOCKS,
  ]),
  shipping: new Set([
    "address",
    "shipping_method",
    "order_notes",
    "gift_message",
    "shipping_insurance",
    "vat_id_field",
    "po_number",
    "delivery_date",
    "pickup_point",
    ...SHARED_SECTION_BLOCKS,
  ]),
  offers: new Set([
    "upsell_slot",
    "recommendation_cards",
    "incentive_banner",
    "testimonial_cards",
    ...SHARED_SECTION_BLOCKS,
  ]),
  payment: new Set([
    "payment_method_selector",
    "billing_same_as_shipping",
    "save_card_toggle",
    "terms_checkbox",
    "age_verification",
    "subscription_consent",
    "pay_button",
    ...SHARED_SECTION_BLOCKS,
  ]),
  legal: new Set([
    "legal_links",
    "cookie_notice",
    ...SHARED_SECTION_BLOCKS,
  ]),
  support: new Set([
    "support_block",
    "help_center_link",
    "benefit_rows",
    "testimonial_cards",
    ...SHARED_SECTION_BLOCKS,
  ]),
  summary: new Set([
    "line_items",
    "totals",
    "promo_code",
    "product_thumbnails",
    "tax_breakdown",
    "ic_fee_transparency",
    "gift_card_input",
    "loyalty_login_card",
    "recommendation_cards",
    "benefit_rows",
    "testimonial_cards",
    ...SHARED_SECTION_BLOCKS,
  ]),
};

export const VALID_SECTIONS = new Set([
  "contact",
  "shipping",
  "offers",
  "payment",
  "legal",
  "support",
  "summary",
]);

const REQUIRED_VISIBLE_SECTIONS = ["contact", "shipping", "payment", "summary"];

const FRAUD_PROVIDERS = new Set(["cost_plus", "shopify", "sift", "signifyd"]);

const ROUTING_TYPES = new Set<NodeType>([
  "condition",
  "switch",
  "ab_test",
  "schedule",
  "delay",
  "fraud_check",
]);

const TRIGGER_SOURCE_TYPES = new Set([
  "shopify",
  "api",
  "email",
  "payment_link",
  "woocommerce",
  "prestashop",
  "custom",
]);

const SHOPIFY_DOMAIN_REGEX = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

// ─── Outgoing pointer extraction ─────────────────────────────────────────────

function outgoingEdges(node: FlowNode): { handle: string; target: string }[] {
  switch (node.type) {
    case "trigger":
      return node.next ? [{ handle: "next", target: node.next }] : [];
    case "condition":
      return [
        ...(node.true_next ? [{ handle: "true_next", target: node.true_next }] : []),
        ...(node.false_next ? [{ handle: "false_next", target: node.false_next }] : []),
        ...(node.error_next ? [{ handle: "error_next", target: node.error_next }] : []),
      ];
    case "switch":
      return [
        ...node.branches
          .map((b, i) => (b.next ? { handle: `branch-${i}`, target: b.next } : null))
          .filter((x): x is { handle: string; target: string } => x !== null),
        ...(node.default_next ? [{ handle: "default_next", target: node.default_next }] : []),
        ...(node.error_next ? [{ handle: "error_next", target: node.error_next }] : []),
      ];
    case "ab_test":
      return [
        ...node.variants
          .map((v) => (v.next ? { handle: `variant-${v.id}`, target: v.next } : null))
          .filter((x): x is { handle: string; target: string } => x !== null),
        ...(node.error_next ? [{ handle: "error_next", target: node.error_next }] : []),
      ];
    case "schedule":
      return [
        ...(node.active_next ? [{ handle: "active_next", target: node.active_next }] : []),
        ...(node.inactive_next ? [{ handle: "inactive_next", target: node.inactive_next }] : []),
        ...(node.error_next ? [{ handle: "error_next", target: node.error_next }] : []),
      ];
    case "checkout_page":
      return node.next ? [{ handle: "next", target: node.next }] : [];
    case "capture_policy":
      return [];
    case "delay":
      return [
        { handle: "next", target: node.next },
        ...(node.error_next ? [{ handle: "error_next", target: node.error_next }] : []),
      ].filter((e) => e.target);
    case "fraud_check":
      return [
        ...(node.low_next ? [{ handle: "low_next", target: node.low_next }] : []),
        ...(node.medium_next ? [{ handle: "medium_next", target: node.medium_next }] : []),
        ...(node.high_next ? [{ handle: "high_next", target: node.high_next }] : []),
        ...(node.error_next ? [{ handle: "error_next", target: node.error_next }] : []),
      ];
    case "http_request":
      return [
        ...(node.next ? [{ handle: "next", target: node.next }] : []),
        ...(node.error_next ? [{ handle: "error_next", target: node.error_next }] : []),
      ];
  }
}

// ─── Main entry ──────────────────────────────────────────────────────────────

/**
 * Per-shop conflict info passed to the validator. The Toolbar (which owns
 * the Sources cache) constructs this from the live `useSourcesStore` state
 * so rule 24 can flag drafts that bind a shop already used elsewhere.
 */
export interface TriggerBinding {
  /** Shop domain (or other reference). */
  reference: string;
  /** Owning flow id. */
  flow_id: string;
  /** Owning flow display name. */
  flow_name: string;
}

export interface ValidateFlowOptions {
  /** This flow's id. Bindings owned by THIS flow are not flagged. */
  currentFlowId?: string;
  /** Map of (sourceType, reference) → owning flow. Today only "shopify"
   *  is populated; future adapters layer on. */
  bindings?: { shopify: Record<string, TriggerBinding> };
}

export function validateFlow(
  definition: FlowDefinition,
  options: ValidateFlowOptions = {},
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Rule 2: size limit
  const serialized = JSON.stringify(definition);
  if (serialized.length > MAX_DEFINITION_BYTES) {
    errors.push({
      rule: 2,
      node_id: null,
      code: "FLOW_TOO_LARGE",
      message: `Definition is ${serialized.length} bytes; max is ${MAX_DEFINITION_BYTES}.`,
    });
  }

  // Rule 1: at least one trigger; each trigger has exactly one source;
  // no two triggers share the same (source_type, source_reference);
  // start_node is ONE of the triggers (any). Multiple triggers in the
  // same flow are allowed — they can converge on the same downstream
  // node. See CLAUDE.md rule on multi-trigger engine execution for the
  // runtime caveat (currently only the start_node trigger actually
  // fires; other triggers surface in the designer but require the
  // follow-up engine change to execute).
  const nodeEntries = Object.entries(definition.nodes);
  const triggers = nodeEntries.filter(([, n]) => n.type === "trigger");
  if (triggers.length === 0) {
    errors.push({
      rule: 1,
      node_id: null,
      code: "NO_TRIGGER",
      message: "Flow must have at least one trigger node.",
    });
  }

  // Per-trigger: exactly one source binding.
  for (const [id, node] of triggers) {
    const t = node as TriggerNode;
    const sources = t.sources ?? [];
    if (sources.length > 1) {
      errors.push({
        rule: 1,
        node_id: id,
        code: "TRIGGER_TOO_MANY_SOURCES",
        message:
          "A trigger can only bind one source. Add another trigger node for an additional source instead.",
      });
    }
  }

  // Within this flow: (source_type, source_reference) must be unique
  // across every trigger node. Same source on two triggers = duplicate
  // work at runtime + ambiguous routing.
  const seenSources = new Map<string, string>(); // key → first-seen triggerId
  for (const [id, node] of triggers) {
    const t = node as TriggerNode;
    for (const src of t.sources ?? []) {
      const key = `${src.type}:${(src.reference ?? "").toLowerCase()}`;
      const firstSeen = seenSources.get(key);
      if (firstSeen) {
        errors.push({
          rule: 1,
          node_id: id,
          code: "DUPLICATE_TRIGGER_SOURCE",
          message: `Source "${src.type}:${src.reference}" is already bound by trigger "${firstSeen}" in this flow.`,
        });
      } else {
        seenSources.set(key, id);
      }
    }
  }

  // start_node must point at ONE of the triggers (not required to be
  // "the" trigger — any will do).
  if (triggers.length > 0) {
    const triggerIDs = new Set(triggers.map(([id]) => id));
    if (!triggerIDs.has(definition.start_node)) {
      errors.push({
        rule: 1,
        node_id: null,
        code: "BAD_START_NODE",
        message: `start_node "${definition.start_node}" must point at one of the trigger nodes.`,
      });
    }
  }
  const triggerId = triggers[0]?.[0];

  // Rule 3: schema — required fields per node type
  for (const [id, node] of nodeEntries) {
    validateNodeSchema(id, node, errors);
  }

  // Rule 4: every referenced node id exists
  for (const [id, node] of nodeEntries) {
    for (const { handle, target } of outgoingEdges(node)) {
      if (!definition.nodes[target]) {
        errors.push({
          rule: 4,
          node_id: id,
          code: "DANGLING_POINTER",
          message: `${id}.${handle} points at "${target}" which does not exist.`,
        });
      }
    }
  }
  if (triggerId && !definition.nodes[definition.start_node]) {
    errors.push({
      rule: 4,
      node_id: null,
      code: "START_NODE_MISSING",
      message: `start_node "${definition.start_node}" not in nodes map.`,
    });
  }

  // Rule 5 + 6 — DAG + checkout_page path check + max depth
  if (triggerId && definition.nodes[definition.start_node]) {
    const dagResult = checkDag(definition);
    errors.push(...dagResult);
  }

  // Rule 7: JSONLogic parse — rules are already JSON; structural validity is
  // verified by TypeScript types. Here we do a lightweight sanity pass.
  for (const [id, node] of nodeEntries) {
    if (node.type === "condition" && !isObject(node.rule)) {
      errors.push({
        rule: 7,
        node_id: id,
        code: "BAD_RULE",
        message: "Condition rule must be a JSONLogic object.",
      });
    }
    if (node.type === "switch" && node.expr === undefined) {
      errors.push({
        rule: 7,
        node_id: id,
        code: "BAD_EXPR",
        message: "Switch expr must be defined.",
      });
    }
  }

  // Rule 11: schedule window end > start
  for (const [id, node] of nodeEntries) {
    if (node.type === "schedule") {
      const s = Date.parse(node.window.start);
      const e = Date.parse(node.window.end);
      if (Number.isNaN(s) || Number.isNaN(e)) {
        errors.push({
          rule: 11,
          node_id: id,
          code: "SCHEDULE_BAD_DATES",
          message: "Schedule window has unparseable dates.",
        });
      } else if (!(e > s)) {
        errors.push({
          rule: 11,
          node_id: id,
          code: "SCHEDULE_END_BEFORE_START",
          message: "Schedule window end must be after start.",
        });
      }
    }
  }

  // Rule 12: ab_test weights all non-negative; at least one non-zero
  for (const [id, node] of nodeEntries) {
    if (node.type === "ab_test") {
      if (node.variants.length === 0) {
        errors.push({
          rule: 12,
          node_id: id,
          code: "AB_NO_VARIANTS",
          message: "A/B test must have at least one variant.",
        });
      }
      const bad = node.variants.find((v) => !Number.isInteger(v.weight) || v.weight < 0);
      if (bad) {
        errors.push({
          rule: 12,
          node_id: id,
          code: "AB_BAD_WEIGHT",
          message: `Variant "${bad.id}" has invalid weight ${bad.weight}.`,
        });
      }
      if (node.variants.every((v) => v.weight === 0)) {
        errors.push({
          rule: 12,
          node_id: id,
          code: "AB_ALL_ZERO",
          message: "At least one A/B variant must have weight > 0.",
        });
      }
    }
  }

  // Rule 13 — capture_policy.mode valid; next is null
  for (const [id, node] of nodeEntries) {
    if (node.type === "capture_policy") {
      const valid = ["auto", "manual_on_fulfillment", "manual_explicit"];
      if (!valid.includes(node.mode)) {
        errors.push({
          rule: 13,
          node_id: id,
          code: "CAPTURE_BAD_MODE",
          message: `Capture policy mode "${node.mode}" is not valid.`,
        });
      }
      if (node.next !== null) {
        errors.push({
          rule: 13,
          node_id: id,
          code: "CAPTURE_NOT_TERMINAL",
          message: "capture_policy.next must be null (terminal).",
        });
      }
    }
  }

  // Rules 15 + 16 — capture_policy upstream trace
  for (const [id, node] of nodeEntries) {
    if (node.type === "capture_policy") {
      const ancestors = traceUpstream(definition, id);
      if (ancestors === "cycle") {
        // Rule 5 catches this already
        continue;
      }
      if (ancestors.length === 0) {
        errors.push({
          rule: 15,
          node_id: id,
          code: "CAPTURE_NO_ANCESTOR",
          message: "capture_policy has no upstream checkout_page.",
        });
      } else if (new Set(ancestors).size > 1) {
        errors.push({
          rule: 15,
          node_id: id,
          code: "CAPTURE_CONVERGING_PAGES",
          message: "capture_policy has upstream paths from different checkout_pages.",
        });
      }
    }
  }

  // Rule 17–20 — layout_blocks
  for (const [id, node] of nodeEntries) {
    if (node.type === "checkout_page" && node.layout_blocks) {
      validateLayoutBlocks(id, node.layout_blocks, errors);
    }
  }

  // Rule 21 — delay placement + duration caps
  for (const [id, node] of nodeEntries) {
    if (node.type === "delay") {
      const ancestors = traceUpstream(definition, id);
      if (ancestors !== "cycle" && ancestors.length === 0) {
        errors.push({
          rule: 21,
          node_id: id,
          code: "DELAY_PRE_CHECKOUT",
          message: "Delay nodes are only allowed downstream of a checkout_page.",
        });
      }
      if (node.duration.mode === "fixed") {
        const ms = parseIso8601Duration(node.duration.iso8601);
        if (ms === null) {
          errors.push({
            rule: 21,
            node_id: id,
            code: "DELAY_BAD_DURATION",
            message: `Delay duration "${node.duration.iso8601}" is not a valid ISO 8601 duration.`,
          });
        } else if (ms > 30 * 24 * 3600 * 1000) {
          errors.push({
            rule: 21,
            node_id: id,
            code: "DELAY_TOO_LONG",
            message: "Delay exceeds 30-day maximum.",
          });
        }
      }
    }
  }

  // Rule 22 — fraud_check provider + band outlets
  for (const [id, node] of nodeEntries) {
    if (node.type === "fraud_check") {
      if (!FRAUD_PROVIDERS.has(node.provider)) {
        errors.push({
          rule: 22,
          node_id: id,
          code: "FRAUD_BAD_PROVIDER",
          message: `Fraud provider "${node.provider}" is not recognized.`,
        });
      }
      if (!node.low_next || !node.medium_next || !node.high_next) {
        errors.push({
          rule: 22,
          node_id: id,
          code: "FRAUD_MISSING_BRANCH",
          message: "Fraud check must connect all three band outlets.",
        });
      }
    }
  }

  // Rule 23 — error_next no self-loops (DAG check handles cycles via edges)
  for (const [id, node] of nodeEntries) {
    const errTarget = (node as { error_next?: string }).error_next;
    if (errTarget === id) {
      errors.push({
        rule: 23,
        node_id: id,
        code: "ERROR_NEXT_SELF_LOOP",
        message: "error_next cannot target its own node.",
      });
    }
  }

  // Rule 24 — one-flow-per-source: trigger source references must be unique
  // across the merchant's flows (drafts AND published). Surfaced at edit
  // time so the merchant doesn't waste effort on a flow that won't publish.
  // The orchestrator re-runs the same check at publish time as the
  // authoritative gate; this rule depends on a fresh sourcesStore cache.
  if (options.bindings && triggerId) {
    const trigger = definition.nodes[triggerId] as TriggerNode | undefined;
    if (trigger?.sources) {
      for (const src of trigger.sources) {
        if (src.type !== "shopify" || !src.reference) continue;
        const existing = options.bindings.shopify[src.reference];
        if (existing && existing.flow_id !== options.currentFlowId) {
          errors.push({
            rule: 24,
            node_id: triggerId,
            code: "TRIGGER_CONFLICT",
            message: `Shopify shop "${src.reference}" is already used by flow "${existing.flow_name}". Remove it from that flow before binding it here.`,
          });
        }
      }
    }
  }

  return errors;
}

function validateNodeSchema(
  id: string,
  node: FlowNode,
  errors: ValidationError[],
): void {
  switch (node.type) {
    case "trigger": {
      const t = node as TriggerNode;
      if (!Array.isArray(t.sources)) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "TRIGGER_BAD_SOURCES",
          message: "Trigger sources must be an array.",
        });
      } else {
        for (const src of t.sources) {
          if (!TRIGGER_SOURCE_TYPES.has(src.type)) {
            errors.push({
              rule: 3,
              node_id: id,
              code: "TRIGGER_BAD_SOURCE_TYPE",
              message: `Unknown trigger source type "${src.type}".`,
            });
          }
          if (src.type === "shopify" && !src.reference) {
            errors.push({
              rule: 3,
              node_id: id,
              code: "TRIGGER_MISSING_REFERENCE",
              message: "Shopify source requires a `.myshopify.com` reference.",
            });
          }
          if (src.type === "shopify" && src.reference && !SHOPIFY_DOMAIN_REGEX.test(src.reference)) {
            errors.push({
              rule: 3,
              node_id: id,
              code: "TRIGGER_BAD_SHOPIFY_DOMAIN",
              message: `Shopify reference "${src.reference}" is not a valid myshopify.com domain.`,
            });
          }
        }
      }
      break;
    }
    case "condition": {
      const c = node as ConditionNode;
      if (!c.true_next || !c.false_next) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "CONDITION_MISSING_BRANCH",
          message: "Condition must have both true_next and false_next.",
        });
      }
      break;
    }
    case "switch": {
      const s = node as SwitchNode;
      if (!s.default_next) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "SWITCH_MISSING_DEFAULT",
          message: "Switch must have a default_next.",
        });
      }
      if (!Array.isArray(s.branches) || s.branches.length === 0) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "SWITCH_NO_BRANCHES",
          message: "Switch must have at least one branch.",
        });
      }
      break;
    }
    case "ab_test": {
      const a = node as ABTestNode;
      if (!a.test_key) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "AB_NO_TEST_KEY",
          message: "A/B test must have a test_key.",
        });
      }
      break;
    }
    case "schedule": {
      const sc = node as ScheduleNode;
      if (!sc.window || !sc.window.start || !sc.window.end) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "SCHEDULE_NO_WINDOW",
          message: "Schedule must have window.start and window.end.",
        });
      }
      if (!sc.active_next || !sc.inactive_next) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "SCHEDULE_MISSING_BRANCH",
          message: "Schedule must have both active_next and inactive_next.",
        });
      }
      break;
    }
    case "checkout_page": {
      const p = node as CheckoutPageNode;
      if (!p.project_id) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "PAGE_NO_PROJECT",
          message: "Checkout page must bind to a Cost+ project.",
        });
      }
      if (!p.theme_id) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "PAGE_NO_THEME",
          message: "Checkout page must have a theme_id.",
        });
      }
      if (!p.payment_methods || p.payment_methods.length === 0) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "PAGE_NO_METHODS",
          message: "Checkout page must enable at least one payment method.",
        });
      }
      break;
    }
    case "capture_policy":
      break; // covered by rule 13
    case "delay": {
      const d = node as DelayNode;
      if (!d.duration) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "DELAY_NO_DURATION",
          message: "Delay must have a duration.",
        });
      }
      break;
    }
    case "fraud_check": {
      const f = node as FraudCheckNode;
      if (!f.provider) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "FRAUD_NO_PROVIDER",
          message: "Fraud check must specify a provider.",
        });
      }
      break;
    }
    case "http_request": {
      const h = node as HttpRequestNode;
      if (!h.url) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "HTTP_NO_URL",
          message: "HTTP request must have a URL.",
        });
      } else if (!/^https?:\/\//i.test(h.url) && !h.url.includes("{{")) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "HTTP_BAD_URL",
          message: "HTTP request URL must start with http:// or https:// (or contain a {{var}} placeholder).",
        });
      }
      if (!h.method) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "HTTP_NO_METHOD",
          message: "HTTP request must have a method.",
        });
      }
      if (!h.next) {
        errors.push({
          rule: 3,
          node_id: id,
          code: "HTTP_NO_NEXT",
          message: "HTTP request must connect its next output to another node.",
        });
      }
      break;
    }
  }
}

function validateLayoutBlocks(
  nodeId: string,
  layout: LayoutBlocks,
  errors: ValidationError[],
): void {
  // Rule 17 — section keys
  for (const section of Object.keys(layout.sections)) {
    if (!VALID_SECTIONS.has(section)) {
      errors.push({
        rule: 17,
        node_id: nodeId,
        code: "LAYOUT_BAD_SECTION",
        message: `Unknown section "${section}".`,
      });
    }
  }
  // Rule 18 — block ids known + placed correctly. Custom HTML is an
  // instance-based block (merchant can add many per section, each with a
  // unique `custom_html_<rand>` id), so we accept any key matching that
  // prefix in addition to the canonical set.
  for (const [section, cfg] of Object.entries(layout.sections)) {
    const valid = VALID_BLOCKS_BY_SECTION[section];
    if (!valid) continue;
    for (const block of cfg.blocks) {
      if (block.startsWith("custom_html_")) continue;
      if (!valid.has(block)) {
        errors.push({
          rule: 18,
          node_id: nodeId,
          code: "LAYOUT_UNKNOWN_BLOCK",
          message: `Block "${block}" not valid in section "${section}".`,
        });
      }
    }
  }
  // Rule 19 — required blocks present
  for (const [section, required] of Object.entries(REQUIRED_BLOCKS_BY_SECTION)) {
    const cfg = layout.sections[section as keyof typeof layout.sections];
    if (!cfg) continue;
    for (const block of required) {
      if (!cfg.blocks.includes(block as (typeof cfg.blocks)[number])) {
        errors.push({
          rule: 19,
          node_id: nodeId,
          code: "LAYOUT_MISSING_REQUIRED",
          message: `Section "${section}" is missing required block "${block}".`,
        });
      }
    }
  }
  // Rule 20 — required sections visible
  for (const section of REQUIRED_VISIBLE_SECTIONS) {
    const cfg = layout.sections[section as keyof typeof layout.sections];
    if (cfg && !cfg.visible && section !== "shipping") {
      errors.push({
        rule: 20,
        node_id: nodeId,
        code: "LAYOUT_REQUIRED_SECTION_HIDDEN",
        message: `Section "${section}" must be visible.`,
      });
    }
  }
}

// ─── DAG + depth check ──────────────────────────────────────────────────────

function checkDag(definition: FlowDefinition): ValidationError[] {
  const errors: ValidationError[] = [];
  const visited = new Set<string>();
  const onStack = new Set<string>();
  const checkoutPageReached: Record<string, boolean> = {};

  function walk(id: string, depth: number, seenCheckoutPage: boolean): void {
    if (depth > MAX_DEPTH) {
      errors.push({
        rule: 6,
        node_id: id,
        code: "MAX_DEPTH_EXCEEDED",
        message: `Path exceeds ${MAX_DEPTH} nodes.`,
      });
      return;
    }
    if (onStack.has(id)) {
      errors.push({
        rule: 5,
        node_id: id,
        code: "CYCLE",
        message: `Cycle detected through node ${id}.`,
      });
      return;
    }
    if (visited.has(id)) return;
    onStack.add(id);
    visited.add(id);

    const node = definition.nodes[id];
    if (!node) {
      onStack.delete(id);
      return;
    }
    const reachedHere = seenCheckoutPage || node.type === "checkout_page";
    if (node.type === "checkout_page") {
      checkoutPageReached[id] = true;
    }

    const out = outgoingEdges(node);
    if (out.length === 0) {
      // Terminal — must be a checkout_page or a capture_policy
      if (node.type !== "checkout_page" && node.type !== "capture_policy") {
        errors.push({
          rule: 5,
          node_id: id,
          code: "BAD_TERMINAL",
          message: `Terminal node "${id}" is of type ${node.type}; must be checkout_page or capture_policy.`,
        });
      }
      if (!reachedHere) {
        errors.push({
          rule: 5,
          node_id: id,
          code: "NO_CHECKOUT_PAGE",
          message: `Path reaching "${id}" never passes through a checkout_page.`,
        });
      }
    }

    for (const e of out) {
      walk(e.target, depth + 1, reachedHere);
    }
    onStack.delete(id);
  }

  walk(definition.start_node, 1, false);

  // Unreachable nodes (from start_node) — don't fail, but flag
  for (const id of Object.keys(definition.nodes)) {
    if (!visited.has(id)) {
      errors.push({
        rule: 5,
        node_id: id,
        code: "UNREACHABLE",
        message: `Node "${id}" is not reachable from start_node.`,
      });
    }
  }

  return errors;
}

function traceUpstream(
  definition: FlowDefinition,
  targetId: string,
): string[] | "cycle" {
  // Return the nearest non-routing ancestor(s), deduplicated as a list of
  // node ids (expected to all be the same checkout_page for rule 15).
  const ancestors = new Set<string>();
  const visiting = new Set<string>();

  function walkBackwards(id: string): void {
    if (visiting.has(id)) return;
    visiting.add(id);
    for (const [parentId, parent] of Object.entries(definition.nodes)) {
      const edges = outgoingEdges(parent);
      if (edges.some((e) => e.target === id)) {
        if (ROUTING_TYPES.has(parent.type)) {
          walkBackwards(parentId);
        } else if (parent.type === "checkout_page") {
          ancestors.add(parentId);
        }
      }
    }
  }

  walkBackwards(targetId);
  return [...ancestors];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/**
 * Minimal ISO 8601 duration parser covering the forms Cost+ uses
 * ("PT24H", "P7D", "P1Y2M3DT4H5M6S", etc). Returns milliseconds, or null on
 * parse failure.
 */
function parseIso8601Duration(s: string): number | null {
  const m = s.match(
    /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/,
  );
  if (!m) return null;
  const [, years, months, days, hours, minutes, seconds] = m;
  const y = Number.parseInt(years ?? "0", 10);
  const mo = Number.parseInt(months ?? "0", 10);
  const d = Number.parseInt(days ?? "0", 10);
  const h = Number.parseInt(hours ?? "0", 10);
  const mi = Number.parseInt(minutes ?? "0", 10);
  const se = Number.parseInt(seconds ?? "0", 10);
  // Approximate months as 30 days, years as 365 — fine for the 30-day cap check.
  return (
    ((y * 365 + mo * 30 + d) * 24 * 3600 + h * 3600 + mi * 60 + se) * 1000
  );
}
